import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { getNextPo, patchTenantSettings } from '../api/client.js';
import { Card, CardContent } from './ui/card.js';
import { Input } from './ui/input.js';
import { Skeleton } from './ui/skeleton.js';

export function JobNumberingSection() {
  const queryClient = useQueryClient();
  const { data: nextPoData, isLoading } = useQuery({
    queryKey: ['next-po'],
    queryFn: getNextPo,
  });

  const [value, setValue] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (nextPoData) {
      setValue(String(nextPoData.nextPoNumber));
    }
  }, [nextPoData]);

  const mutation = useMutation({
    mutationFn: (nextPoNumber: number) => patchTenantSettings({ nextPoNumber }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['next-po'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const handleSave = () => {
    const num = Number(value);
    if (Number.isNaN(num) || num < 1 || !Number.isInteger(num)) return;
    if (nextPoData && num === nextPoData.nextPoNumber) return;
    mutation.mutate(num);
  };

  return (
    <Card>
        <CardContent className="p-6">
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-xs">
              <label htmlFor="next-po" className="eyebrow mb-1.5 block">
                Next PO number
              </label>
              {isLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    id="next-po"
                    type="number"
                    min="1"
                    step="1"
                    value={value}
                    onChange={(e) => {
                      setValue(e.target.value);
                      setSaved(false);
                    }}
                    onBlur={handleSave}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSave();
                    }}
                    disabled={mutation.isPending}
                    className="w-40 font-mono"
                  />
                  {saved && <Check className="h-4 w-4 text-primary" />}
                </div>
              )}
            </div>
          </div>
          <p className="mt-2 text-xs text-taupe">
            New jobs get this PO number automatically. It increments after each assignment.
            You can override per-job when creating.
          </p>
          {mutation.error && (
            <div className="mt-2 text-xs text-destructive">
              {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
            </div>
          )}
        </CardContent>
      </Card>
  );
}
